import { useState, useRef, useEffect } from 'react';
import { useBoardStore } from '../../stores/boardStore';
import { Avatar } from '../common/Avatar';
import { Button } from '../common/Button';
import { CreateCardModal } from '../CardModal/CreateCardModal';
import { InviteManager } from '../Auth/InviteManager';
import { BoardSelector } from './BoardSelector';
import { api } from '../../api/client';

export function Header() {
  const { board, currentUser, tags, searchQuery, setSearchQuery, search, clearSearch, searchResults, fetchBoards } = useBoardStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showInviteManager, setShowInviteManager] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const selectedTag = tags.find(t => t.id === selectedTagId);

  // Fetch boards on mount
  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (query.trim()) {
      searchTimeout.current = setTimeout(() => {
        search(query);
      }, 300);
    } else {
      clearSearch();
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
      window.location.reload();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleTagSelect = (tagId: string | null) => {
    setSelectedTagId(tagId);
    setShowTagFilter(false);
    window.dispatchEvent(new CustomEvent('tagFilterChange', { detail: tagId }));
  };

  useEffect(() => {
    clearSearch();

    if (selectedTagId) {
      setSelectedTagId(null);
      window.dispatchEvent(new CustomEvent('tagFilterChange', { detail: null }));
    }
  }, [board?.id, clearSearch, selectedTagId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          searchInputRef.current?.focus();
        }
      }
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setShowCreateModal(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="bg-white/72 backdrop-blur-xl border-b border-white/50 px-3 sm:px-6 py-3 sm:py-4 shadow-[0_8px_30px_rgba(15,76,129,0.08)] sticky top-0 z-50">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Logo and board selector */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="flex items-center gap-2 shrink-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" viewBox="0 0 100 100" fill="currentColor">
                <rect width="100" height="100" rx="12" />
                <rect x="15" y="20" width="20" height="60" rx="4" fill="white" opacity="0.9" />
                <rect x="40" y="30" width="20" height="50" rx="4" fill="white" opacity="0.7" />
                <rect x="65" y="25" width="20" height="55" rx="4" fill="white" opacity="0.8" />
              </svg>
              <span className="hidden sm:inline font-semibold text-gray-800">Docanban</span>
            </div>
            <span className="hidden sm:inline text-gray-300">|</span>
            <BoardSelector currentBoard={board} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Tag Filter - Desktop */}
            <div className="hidden md:block relative">
              <button
                onClick={() => setShowTagFilter(!showTagFilter)}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${selectedTagId
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-sm'
                  }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                {selectedTag ? (
                  <span className="flex items-center gap-1">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: selectedTag.color }}
                    />
                    {selectedTag.name}
                  </span>
                ) : (
                  <span>Filter</span>
                )}
                {selectedTagId && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagSelect(null);
                    }}
                    className="ml-1 hover:bg-blue-200 rounded p-0.5"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </button>

              {/* Tag dropdown */}
              {showTagFilter && (
                <>
                  <div className="fixed inset-0" onClick={() => setShowTagFilter(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                    <button
                      onClick={() => handleTagSelect(null)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${!selectedTagId ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
                    >
                      All Documents
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    {tags.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-400">No tags created yet</div>
                    ) : (
                      tags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleTagSelect(tag.id)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${selectedTagId === tag.id ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
                        >
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Search - Desktop */}
            <div className="hidden md:block relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search... (/)"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-40 lg:w-56 px-4 py-2 pl-10 text-sm bg-gray-100 border border-transparent rounded-lg focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-200"
              />
              <svg
                className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Search results dropdown */}
              {searchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-96 overflow-y-auto z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  {searchResults.map((card) => (
                    <button
                      key={card.id}
                      onClick={() => {
                        useBoardStore.getState().selectCard(card.id);
                        clearSearch();
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900">{card.title}</div>
                          {card.description && (
                            <div className="text-sm text-gray-500 truncate mt-0.5">{card.description}</div>
                          )}
                          {card.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {card.tags.slice(0, 2).map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                                  style={{ backgroundColor: tag.color }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                          Open
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchResults && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 z-50">
                  No documents found
                </div>
              )}
            </div>

            {/* Mobile buttons */}
            <button
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            {/* Create button */}
            <Button onClick={() => setShowCreateModal(true)} className="shrink-0 shadow-sm hover:shadow-md transition-shadow">
              <svg className="w-4 h-4 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">New Doc</span>
            </Button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 hover:bg-gray-100 rounded-lg p-1"
              >
                {currentUser && (
                  <Avatar name={currentUser.name} color={currentUser.avatar_color} />
                )}
              </button>

              {showUserMenu && (
                <>
                  <div className="fixed inset-0" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="font-medium text-gray-900 truncate">{currentUser?.name}</div>
                      <div className="text-sm text-gray-500 truncate">{currentUser?.email}</div>
                      <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {currentUser?.tier === 'pro' ? 'Pro' : 'Standard'} · {currentUser?.role}
                      </div>
                    </div>

                    {currentUser?.tier === 'pro' && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowInviteManager(true);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        Manage Team
                      </button>
                    )}

                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile search bar */}
        {showMobileSearch && (
          <div className="md:hidden mt-3 space-y-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
                className="w-full px-4 py-2 pl-10 text-sm bg-gray-100 border border-transparent rounded-lg focus:bg-white focus:border-gray-300 focus:outline-none"
              />
              <svg
                className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Mobile Tag Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleTagSelect(null)}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${!selectedTagId
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-600'
                  }`}
              >
                All
              </button>
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagSelect(tag.id)}
                  className={`px-3 py-1.5 text-xs rounded-full flex items-center gap-1.5 transition-colors ${selectedTagId === tag.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                    }`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile tag filter pills - always visible when tags exist */}
        {tags.length > 0 && !showMobileSearch && (
          <div className="md:hidden mt-2 flex items-center gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-hide">
            <button
              onClick={() => handleTagSelect(null)}
              className={`shrink-0 px-3 py-1 text-xs rounded-full transition-colors ${
                !selectedTagId ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600'
              }`}
            >
              All
            </button>
            {tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagSelect(tag.id)}
                className={`shrink-0 px-3 py-1 text-xs rounded-full flex items-center gap-1.5 transition-colors ${
                  selectedTagId === tag.id ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </header>

      {showCreateModal && (
        <CreateCardModal onClose={() => setShowCreateModal(false)} />
      )}

      {showInviteManager && currentUser && (
        <InviteManager
          currentUser={currentUser}
          onClose={() => setShowInviteManager(false)}
        />
      )}
    </>
  );
}
